// Routes and providers (the signed-in session comes from AuthGate, FND-C-10). One route per page of the specification; a page the role may not open redirects to the
// role's home (GEN-01: hidden in the menus and refused by the router; the database refuses the operations).
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import '../i18n';
import { KitGallery } from '../design/KitGallery';
import { SettingsPage } from '../features/settings/SettingsPage';
import { ProductCard } from '../features/catalog/ProductCard';
import { ProductsPage } from '../features/catalog/ProductsPage';
import { CustomerCard, CustomersPage } from '../features/partners/Customers';
import { SupplierCard, SuppliersPage } from '../features/partners/Suppliers';
import { InventoryPage, ReconcileCard, StockCard } from '../features/stock/InventoryPage';
import { RoundPage } from '../features/rounds/RoundPage';
import { RoundCloseCard } from '../features/rounds/RoundClose';
import { PreOrdersPage } from '../features/orders/PreOrdersPage';
import { DashboardPage } from '../features/reports/DashboardPage';
import { QueuePage } from '../features/offline/QueuePage';
import { ReportsPage } from '../features/reports/ReportsPage';
import { ReturnCard, ReturnsPage } from '../features/returns/ReturnsPage';
import { TrucksPage } from '../features/rounds/TrucksPage';
import { InvoiceCard, PromotionsPage, SalesPage } from '../features/sales/SalesPage';
import { CapitalPage } from '../features/cash/CapitalPage';
import { OrderCard, PurchasingPage } from '../features/purchasing/PurchasingPage';
import { ExpensesPage } from '../features/expenses/ExpensesPage';
import { AgreementsPage, SellerCard, SellersPage } from '../features/expenses/SellerAccountsPage';
import { PAGES, canOpen, homeOf, type PageDef } from './pages';
import { PagePlaceholder } from './PagePlaceholder';
import { AuthGate } from './AuthGate';
import { useSession } from './session';
import { ToastHost } from './ToastHost';
import { Shell } from './Shell';

// Pages built so far; the others show their placeholder until their phase.
const BUILT: Record<string, () => JSX.Element> = {
  settings: SettingsPage,
  products: ProductsPage,
  suppliers: SuppliersPage,
  customers: CustomersPage,
  inventory: InventoryPage,
  capital: CapitalPage,
  purchasing: PurchasingPage,
  trucks: TrucksPage,
  round: RoundPage,
  sales: SalesPage,
  promotions: PromotionsPage,
  expenses: ExpensesPage,
  sellers: SellersPage,
  returns: ReturnsPage,
  orders: PreOrdersPage,
  dashboard: DashboardPage,
  reports: ReportsPage,
  agreements: AgreementsPage,
};
// Card pages open from a list and belong to its page (same roles).
const CARDS: { page: string; element: () => JSX.Element; path?: string }[] = [
  { page: 'inventory', element: ReconcileCard, path: '/inventory/reconcile' },
  { page: 'products', element: ProductCard },
  { page: 'suppliers', element: SupplierCard },
  { page: 'customers', element: CustomerCard },
  { page: 'inventory', element: StockCard },
  { page: 'purchasing', element: OrderCard },
  { page: 'sales', element: InvoiceCard },
  { page: 'sellers', element: SellerCard, path: '/sellers/:id' },
  { page: 'returns', element: ReturnCard, path: '/returns/:id' },
  { page: 'round', element: RoundCloseCard, path: '/round/closed/:id' },
  { page: 'round', element: QueuePage, path: '/round/queue' },
];
function PageBody({ page }: { page: PageDef }) {
  const Built = BUILT[page.id];
  return Built ? <Built /> : <PagePlaceholder page={page} />;
}

export function AppRoutes() {
  const { role } = useSession();
  const { search } = useLocation();
  const home = { pathname: homeOf(role).path, search };
  return (
    <Routes>
      <Route path="/" element={<Navigate to={home} replace />} />
      {PAGES.map((page) => (
        <Route
          key={page.id}
          path={page.path}
          element={
            canOpen(role, page.id) ? (
              <Shell>
                <PageBody page={page} />
              </Shell>
            ) : (
              <Navigate to={home} replace />
            )
          }
        />
      ))}
      {CARDS.map(({ page, element: Card, path }) => (
        <Route
          key={path ?? page}
          path={path ?? `/${page}/:id`}
          element={
            canOpen(role, page) ? (
              <Shell>
                <Card />
              </Shell>
            ) : (
              <Navigate to={home} replace />
            )
          }
        />
      ))}
      {import.meta.env.DEV && role === 'owner' && <Route path="/kit" element={<KitGallery />} />}
      <Route path="*" element={<Navigate to={home} replace />} />
    </Routes>
  );
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1, gcTime: 24 * 3600 * 1000 } },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthGate>
          <AppRoutes />
        </AuthGate>
        <ToastHost />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
